import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import classNames from 'classnames';
import { Theme } from 'helpers/theme';

const useStyles = createUseStyles((theme: Theme) => ({
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 8,
    marginTop: 'auto',
    fontSize: 12,
    color: '#6A728F',
    gap: '12px'
  },
  summary: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0
  },
  pageNumbers: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flexShrink: 0
  },
  pageNum: {
    minWidth: 26,
    height: 26,
    padding: '0 6px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    color: '#6A728F',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background 0.15s, color 0.15s',
    '&:hover': {
      background: '#F4F5F7'
    }
  },
  pageNumActive: {
    background: '#0950ee',
    color: 'white',
    '&:hover': {
      background: '#0a4cdf'
    }
  },
  pageArrow: {
    extend: 'pageNum',
    fontSize: 14
  },
  pageArrowDisabled: {
    color: '#C7CBD8',
    cursor: 'default',
    '&:hover': {
      background: 'transparent'
    }
  },
  ellipsis: {
    minWidth: 18,
    color: '#9aa0b8',
    userSelect: 'none',
    fontSize: 12
  }
}));

type Props = {
  page: number;
  pageSize: number;
  totalItems: number;
  onChange: (page: number) => void;
  siblings?: number;
};

function buildPages(
  current: number,
  total: number,
  siblings: number
): Array<number | 'ellipsis'> {
  const pages: Array<number | 'ellipsis'> = [];
  const first = 0;
  const last = total - 1;

  const rangeStart = Math.max(first + 1, current - siblings);
  const rangeEnd = Math.min(last - 1, current + siblings);

  pages.push(first);

  if (rangeStart > first + 1) {
    pages.push('ellipsis');
  }

  for (let i = rangeStart; i <= rangeEnd; i++) {
    pages.push(i);
  }

  if (rangeEnd < last - 1) {
    pages.push('ellipsis');
  }

  if (last > first) {
    pages.push(last);
  }

  return pages;
}

export default function CardPagination({
  page,
  pageSize,
  totalItems,
  onChange,
  siblings = 1
}: Props) {
  const theme = useTheme();
  const classes = useStyles({ theme });

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const start = safePage * pageSize;

  if (totalItems <= pageSize) return null;

  const pages = buildPages(safePage, totalPages, siblings);

  return (
    <div className={classes.pagination}>
      <div className={classes.summary}>
        Showing {start + 1}–{Math.min(start + pageSize, totalItems)} of{' '}
        {totalItems}
      </div>
      <div className={classes.pageNumbers}>
        <div
          className={classNames(classes.pageArrow, {
            [classes.pageArrowDisabled]: safePage === 0
          })}
          onClick={() => safePage > 0 && onChange(safePage - 1)}
        >
          ‹
        </div>
        {pages.map((p, i) =>
          p === 'ellipsis' ? (
            <div key={`e${i}`} className={classes.ellipsis}>
              …
            </div>
          ) : (
            <div
              key={p}
              className={classNames(classes.pageNum, {
                [classes.pageNumActive]: p === safePage
              })}
              onClick={() => onChange(p)}
            >
              {p + 1}
            </div>
          )
        )}
        <div
          className={classNames(classes.pageArrow, {
            [classes.pageArrowDisabled]: safePage >= totalPages - 1
          })}
          onClick={() =>
            safePage < totalPages - 1 && onChange(safePage + 1)
          }
        >
          ›
        </div>
      </div>
    </div>
  );
}
